import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import styles from './AdminVerifyEmail.module.css';

const VERIFY_USER_EMAIL_URL =
  'https://us-central1-cheriecoiffer-react.cloudfunctions.net/verifyUserEmail';
const LIST_USERS_URL =
  'https://us-central1-cheriecoiffer-react.cloudfunctions.net/listUsers';

function AdminVerifyEmail() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [verifyingUid, setVerifyingUid] = useState(null);
  const [error, setError] = useState('');

  // Charger la liste des utilisateurs au montage (seulement si admin)
  useEffect(() => {
    if (!currentUser || currentUser.displayName?.toLowerCase() !== 'admin') {
      return;
    }

    const fetchUsers = async () => {
      setLoadingList(true);
      setError('');
      try {
        const response = await fetch(LIST_USERS_URL);
        const data = await response.json();

        if (response.ok && data.success) {
          // Trier par date de création décroissante (plus récents en premier)
          const sortedUsers = [...(data.users || [])].sort((a, b) => {
            const timeA = a.creationTime ? new Date(a.creationTime).getTime() : 0;
            const timeB = b.creationTime ? new Date(b.creationTime).getTime() : 0;
            return timeB - timeA;
          });
          setUsers(sortedUsers);
        } else {
          setError(data.error || 'Erreur lors du chargement des utilisateurs');
        }
      } catch (err) {
        setError(`Erreur : ${err.message}`);
      } finally {
        setLoadingList(false);
      }
    };

    fetchUsers();
  }, [currentUser]);

  // Vérifier que l'utilisateur est admin (après les hooks)
  if (!currentUser || currentUser.displayName?.toLowerCase() !== 'admin') {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <h1>Accès refusé</h1>
          <p>Vous devez être administrateur pour accéder à cette page.</p>
          <button type="button" onClick={() => navigate('/')}>
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  const handleVerify = async (uid, email) => {
    setVerifyingUid(uid);
    setError('');

    try {
      const response = await fetch(VERIFY_USER_EMAIL_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Mettre à jour le statut dans la liste locale
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.uid === uid ? { ...user, emailVerified: true } : user
          )
        );
      } else {
        setError(data.error || 'Erreur lors de la vérification');
      }
    } catch (err) {
      setError(`Erreur : ${err.message}`);
    } finally {
      setVerifyingUid(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return 'N/A';
    }
  };

  const verifiedCount = users.filter((u) => u.emailVerified).length;
  const unverifiedCount = users.filter((u) => !u.emailVerified).length;

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <h1 className={styles.title}>Gestion des utilisateurs</h1>
        <p className={styles.subtitle}>
          Liste de tous les utilisateurs avec leur statut de vérification d'email.
        </p>

        {error && (
          <div className={styles.errorMessage}>
            {error}
          </div>
        )}

        {loadingList ? (
          <div className={styles.loading}>Chargement des utilisateurs...</div>
        ) : (
          <>
            <div className={styles.stats}>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Total :</span>
                <span className={styles.statValue}>{users.length}</span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Vérifiés :</span>
                <span className={`${styles.statValue} ${styles.statVerified}`}>
                  {verifiedCount}
                </span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Non vérifiés :</span>
                <span className={`${styles.statValue} ${styles.statUnverified}`}>
                  {unverifiedCount}
                </span>
              </div>
            </div>

            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Pseudo</th>
                    <th>Type user</th>
                    <th>Type compte</th>
                    <th>Payment status</th>
                    <th>Instagram</th>
                    <th>TikTok</th>
                    <th>WhatsApp</th>
                    <th>Statut email</th>
                    <th>Date de création</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan="11" className={styles.noData}>
                        Aucun utilisateur trouvé
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <tr
                        key={user.uid}
                        className={user.emailVerified ? styles.rowVerified : styles.rowUnverified}
                      >
                        <td className={styles.emailCell}>
                          {user.profileId ? (
                            <button
                              type="button"
                              className={styles.profileLink}
                              onClick={() => navigate(`/hairstylist/${user.profileId}`)}
                              title="Voir le profil"
                            >
                              {user.email || 'N/A'}
                            </button>
                          ) : (
                            user.email || 'N/A'
                          )}
                        </td>
                        <td>
                          {user.profileId ? (
                            <button
                              type="button"
                              className={styles.profileLink}
                              onClick={() => navigate(`/hairstylist/${user.profileId}`)}
                              title="Voir le profil"
                            >
                              {user.displayName || '-'}
                            </button>
                          ) : (
                            user.displayName || '-'
                          )}
                        </td>
                        <td className={styles.typeCell}>{user.userType ?? '-'}</td>
                        <td className={styles.typeCell}>{user.accountType ?? '-'}</td>
                        <td className={styles.typeCell}>{user.paymentStatus ?? '-'}</td>
                        <td className={styles.socialCell}>
                          {user.instagram ? (
                            <a
                              href={`https://instagram.com/${user.instagram.replace('@', '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.socialLink}
                            >
                              @{user.instagram.replace('@', '')}
                            </a>
                          ) : '-'}
                        </td>
                        <td className={styles.socialCell}>
                          {user.tiktok ? (
                            <a
                              href={`https://tiktok.com/@${user.tiktok.replace('@', '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.socialLink}
                            >
                              @{user.tiktok.replace('@', '')}
                            </a>
                          ) : '-'}
                        </td>
                        <td className={styles.socialCell}>
                          {user.whatsapp ? (
                            <a
                              href={`https://wa.me/${user.whatsapp.replace(/[^0-9]/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.socialLink}
                            >
                              {user.whatsapp}
                            </a>
                          ) : '-'}
                        </td>
                        <td>
                          <span
                            className={`${styles.statusBadge} ${
                              user.emailVerified ? styles.badgeVerified : styles.badgeUnverified
                            }`}
                          >
                            {user.emailVerified ? '✅ Vérifié' : '❌ Non vérifié'}
                          </span>
                        </td>
                        <td className={styles.dateCell}>{formatDate(user.creationTime)}</td>
                        <td>
                          {!user.emailVerified ? (
                            <button
                              type="button"
                              onClick={() => handleVerify(user.uid, user.email)}
                              disabled={verifyingUid === user.uid}
                              className={styles.verifyButton}
                            >
                              {verifyingUid === user.uid ? 'Vérification...' : 'Vérifier'}
                            </button>
                          ) : (
                            <span className={styles.alreadyVerified}>Déjà vérifié</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        <button
          type="button"
          onClick={() => navigate('/')}
          className={styles.backButton}
        >
          Retour à l'accueil
        </button>
      </div>
    </div>
  );
}

export default AdminVerifyEmail;

